import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useDirectorContext } from "@contexts/director";

import CashChart from "../../components/director/dashboard/CashChart";
import MarginTable from "../../components/director/dashboard/MarginTable";
import ObrasTable from "../../components/director/dashboard/ObrasTable";
import {
  COSTS_UNAVAILABLE,
  annualRate,
  blockAvailable,
  blockReason,
  monthLabel,
  percent,
  scopeRows,
  shareBps,
  signedMoney,
  money,
} from "../../components/director/dashboard/format";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import DirectorPage from "../../components/director/page/DirectorPage";
import { dashboardAnswer } from "../../components/director/page/answers";
import readingWarnings from "../../components/director/page/readingWarnings";
import { formatDayMonth } from "../../utils/directorFormat";
import { primaryScreen } from "./complementaryPages";
import { companyQuery } from "./module1Utils";

const SCREEN = primaryScreen("dashboard");

const SIENGE_ONLY_READING = "Leitura: só dados do Sienge. VPL, TIR e margem completa aparecem quando o valor das unidades a vender for informado.";

const rateAndTax = (data) => ` · taxa ${annualRate(data.rate?.bps)}${data.rate?.provisional ? " · taxa provisória" : ""}`
  + ` · impostos ${percent(data.taxBps)}`;

const readingLine = (data) => {
  if (data.readingMode !== "with_premises") {
    // Premissas salvas sem unidades a vender: taxa e impostos já mudam a meta, então a leitura diz quais são.
    return data.premises ? `${SIENGE_ONLY_READING}${rateAndTax(data)} (premissas salvas)` : SIENGE_ONLY_READING;
  }
  const totals = data.totals || {};
  return `Leitura: Sienge + premissas do diretor (${totals.obrasWithStock} de ${totals.obrasCount} obras com unidades a vender informadas)`
    + `${rateAndTax(data)}.`;
};

const SCOPES = [
  ["obras", "Só obras"],
  ["all", "Tudo, com despesas da empresa"],
];

const isCents = (value) => Number.isSafeInteger(value);

// Pior mês do desencaixe entre as obras (o total não traz o mês).
const worstGapMonth = (data) => (data.obras || [])
  .filter((obra) => isCents(obra.metrics?.gap?.worstCents) && obra.metrics.gap.worstCents < 0)
  .sort((left, right) => left.metrics.gap.worstCents - right.metrics.gap.worstCents)[0]?.metrics.gap.worstMonth ?? null;

// Os quatro cartões da Visão geral (spec): lucro projetado, exposição máxima, desencaixe em 12
// meses e meta de vendas. VPL, TIR e estouro vão para a tabela e para "Sobre estes números".
export const dashboardCards = (data) => {
  const totals = data.totals || {};
  const rateText = annualRate(data.rate?.bps);
  const provisional = data.rate?.provisional === true ? " · taxa provisória" : "";
  const liquidity = blockAvailable(data, "liquidity");
  const returns = blockAvailable(data, "returns");
  const withPremises = data.readingMode === "with_premises" && isCents(totals.totalCents);
  const worstGap = totals.gap?.worstCents ?? 0;
  const gapMonth = monthLabel(worstGapMonth(data));
  return [
    returns && (withPremises
      ? {
        key: "profit",
        label: "Lucro projetado",
        cents: totals.totalCents,
        tone: totals.totalCents < 0 ? "pressure" : "good",
        note: `Soma ${totals.obrasWithStock} de ${totals.obrasCount} obras com unidades a vender informadas`,
      }
      : {
        key: "profit",
        label: "Lucro projetado",
        value: "—",
        tone: "neutral",
        note: "Aparece quando o valor das unidades a vender for informado nas premissas",
      }),
    liquidity && {
      key: "exposure",
      label: "Exposição máxima",
      ...(totals.exposureCents > 0 ? { cents: totals.exposureCents } : { value: "Nenhuma" }),
      tone: totals.exposureCents > 0 ? "warning" : "good",
      note: totals.exposureCents > 0
        ? "Dinheiro próprio que as obras exigem no pior momento de cada uma"
        : "Nenhuma obra fica com o acumulado negativo",
    },
    liquidity && {
      key: "gap",
      label: "Desencaixe 12 meses",
      ...(worstGap < 0 ? { cents: -worstGap } : { value: "Nenhum" }),
      tone: worstGap < 0 ? "pressure" : "good",
      note: worstGap < 0
        ? `Aporte no pior mês${gapMonth !== "—" ? ` (${gapMonth})` : ""}, somando a falta de cada obra`
        : "Nenhuma obra precisa de aporte nos próximos 12 meses",
    },
    returns && {
      key: "goal",
      label: "Meta de vendas",
      ...(totals.goalCents > 0 ? { cents: totals.goalCents } : { value: "Atingida" }),
      tone: totals.goalCents > 0 ? "neutral" : "good",
      note: totals.goalCents > 0
        ? `Quanto falta vender para empatar a ${rateText}${provisional}`
        : `O que já foi vendido paga as obras a essa taxa${provisional}`,
    },
  ];
};

// "Taxa 12% a.a. · impostos 0% · 0 de 2 obras com unidades a vender · revisão de 12/10".
export const premisesSummaryText = (data) => {
  const totals = data.totals || {};
  const parts = [
    `Taxa ${annualRate(data.rate?.bps)}${data.rate?.provisional ? " (provisória)" : ""}`,
    `impostos ${percent(data.taxBps)}`,
    `${totals.obrasWithStock ?? 0} de ${totals.obrasCount ?? (data.obras || []).length} obras com unidades a vender`,
  ];
  const revision = data.premises?.version > 0 ? formatDayMonth(data.premises.updatedAt) : null;
  parts.push(revision ? `revisão de ${revision}` : "premissas ainda não revisadas");
  return parts.join(" · ");
};

const PremisesSummary = ({ data, companyId }) => <div className="director-premises-summary" aria-label="Premissas em vigor">
  <p>{premisesSummaryText(data)}</p>
  <Link className="director-link-button director-link-button--secondary" to={`/dashboard/director/premises${companyQuery(companyId)}`}>
    Editar premissas
  </Link>
</div>;

const Notes = ({ data }) => {
  const totals = data.totals || {};
  const rateText = annualRate(data.rate?.bps);
  const margin = blockAvailable(data, "margin");
  const overrunShare = shareBps(totals.overrunCents, totals.budgetIndexedCents);
  let returnsLine = "VPL e TIR aparecem por obra quando o valor das unidades a vender for informado nas premissas.";
  if (data.readingMode === "with_premises" && isCents(totals.npvCents)) {
    let irr = annualRate(totals.irrBps);
    if (totals.irrAmbiguous) irr = "ambígua (use o VPL)";
    returnsLine = `VPL a ${rateText}: ${money(totals.npvCents)} · TIR ${irr} · lucro, VPL e TIR somam `
      + `${totals.obrasWithStock} de ${totals.obrasCount} obras com unidades a vender informadas.`;
  }
  let overrunLine = null;
  if (margin) {
    overrunLine = `Estouro de custo (tendência do Ampli Obras contra o orçado indexado): ${signedMoney(totals.overrunCents)}`
      + `${isCents(totals.overrunCents) && overrunShare !== null ? ` (${percent(overrunShare)})` : ""}.`;
  } else if (blockReason(data, "margin") === "costs_unavailable") {
    overrunLine = `Estouro de custo: ${COSTS_UNAVAILABLE}`;
  }
  return <>
    <p>{readingLine(data)}</p>
    {data.asOf && <p>{`Data-base: ${monthLabel(data.asOf)}.`}</p>}
    <p>{returnsLine}</p>
    {overrunLine && <p>{overrunLine}</p>}
    <p>Exposição e desencaixe somam obra a obra: SPEs diferentes não compensam caixa entre si.</p>
    {data.rate?.provisional && <p>A taxa de desconto ainda é provisória: passa a valer como taxa da empresa ao salvar as premissas.</p>}
  </>;
};

const DirectorDashboard = () => {
  const { selectedCompanyId, accessByCompany = {}, refreshStatus = null } = useDirectorContext();
  const access = accessByCompany[selectedCompanyId] || {};
  const [scope, setScope] = useState("obras");
  const [focus, setFocus] = useState(null);
  const query = useMemo(() => ({ scope }), [scope]);
  const surface = useDirectorSurface("dashboard", query);
  const { data } = surface;

  const rows = useMemo(() => scopeRows(data), [data]);
  const focusKey = rows.some((row) => row.key === focus) ? focus : null;
  const partialScope = access.completePortfolioScope === false;

  return <DirectorPage
    className="director-dashboard"
    title={SCREEN.label}
    question={SCREEN.question}
    showProjectFilter={false}
    onPublished={surface.reload}
    status={<ModuleState {...surface} onRetry={surface.reload} />}
    qualitySources={[surface]}
    warnings={data ? readingWarnings({ refreshStatus, access }) : []}
    answer={data ? dashboardAnswer(data, { partialScope }) : null}
    cards={data ? dashboardCards(data) : []}
    action={<section className="director-dashboard-bar" aria-label="Premissas e alcance">
      {data && <PremisesSummary data={data} companyId={selectedCompanyId} />}
      <div className="director-owner-switch" role="group" aria-label="Alcance">
        {SCOPES.map(([value, label]) => <button
          key={value}
          type="button"
          aria-pressed={scope === value}
          onClick={() => setScope(value)}
        >
          {label}
        </button>)}
      </div>
    </section>}
    chart={data && blockAvailable(data, "liquidity")
      ? <CashChart data={data} rows={rows} focus={focusKey} onFocus={setFocus} />
      : null}
    detail={data ? <>
      <ObrasTable data={data} rows={rows} focus={focusKey} onFocus={setFocus} />
      {blockAvailable(data, "margin") && <MarginTable data={data} />}
      {blockReason(data, "margin") === "costs_unavailable" && <p className="director-dashboard-margin-unavailable">{COSTS_UNAVAILABLE}</p>}
    </> : null}
    notes={data ? <Notes data={data} /> : null}
  />;
};

export default DirectorDashboard;
